import React, { useState, useEffect, useRef, useCallback, } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import {
  FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight,
  FaTimes, FaPlay, FaMicrochip, FaPause, FaTerminal
} from 'react-icons/fa';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import GestureController from '../../ui/GestureController'; // <-- Импорт контроллера
import MatrixRain from './MatrixRain';
import './Snake.css';
import '../../ui/ArcadeShared.css'; // Общие стили досье

// Дефолт, если сервер не прислал (фоллбэк)
const DEFAULT_GRID_SIZE = 20;

// КОНСТАНТА УСКОРЕНИЯ
// Количество шагов, которое змея делает мгновенно при нажатии той же кнопки.
// Можно изменить на 2, 3 и т.д.
const BOOST_STEPS = 2;

// --- КОМПОНЕНТ СТАРТОВОГО ЭКРАНА ---
const SnakeStartScreen = ({ t, onStart }) => {
  return (
    <div className="tr-report-overlay">
      <div className="sn-start-card">
        <div className="sn-start-icon">
          <FaTerminal />
        </div>
        <div className="sn-start-title">
          {t('arcade.snake_start_title', 'СИСТЕМА ОБНАРУЖЕНА')}
        </div>
        <div className="sn-start-desc">
          {t('arcade.snake_start_desc', 'Взлом системы...')}
        </div>
        <button className="sn-start-btn" onClick={onStart}>
          [{t('arcade.snake_start_btn', 'ВНЕДРИТЬ ВИРУС')}]
        </button>
      </div>
    </div>
  );
};

const Snake = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  // Получаем настройки сессии при старте
  const initialSession = store.ref.info.game.arcadePublic?.session || {};

  // Определяем размер сетки (из сервера или дефолт)
  const gridSize = initialSession.gridSize || DEFAULT_GRID_SIZE;

  // Рассчитываем размер шрифта в зависимости от плотности сетки
  // Чем меньше клеток, тем больше шрифт. Для 20кл ~ 14px, для 10кл ~ 24px.
  const fontSize = Math.max(12, Math.floor(300 / gridSize)) + 'px';

  // Центр поля для старта
  const center = Math.floor(gridSize / 2);

  // --- ИЗМЕНЕНИЕ ЗДЕСЬ: Получаем фразу от сервера ---
  // Вместо random из useMemo, берем из initialSession.textPhrase
  // Добавляем фоллбэк на случай старой версии сервера или ошибки загрузки
  const currentQuote = initialSession.textPhrase || "INITIALIZING CONNECTION... ERROR 404... DATA NOT FOUND...";

  // --- STATE ---
  const [snake, setSnake] = useState([{ x: center, y: center }, { x: center, y: center + 1 }, { x: center, y: center + 2 }]);
  const [food, setFood] = useState({ x: Math.floor(gridSize / 3), y: Math.floor(gridSize / 3) }); // Простая стартовая позиция
  const [score, setScore] = useState(0);

  const [isGameStarted, setIsGameStarted] = useState(false); // <--- НОВОЕ: Ждем старта
  const [isGameOver, setIsGameOver] = useState(false);
  const [showResults, setShowResults] = useState(false); // Для показа окна
  const [isPaused, setIsPaused] = useState(false);

  // Состояния эффектов
  const [isBoosting, setIsBoosting] = useState(false);

  // ИСПРАВЛЕНИЕ: Разделили счетчик (для ключей) и флаг активности (для CSS)
  const [eatSeq, setEatSeq] = useState(0); // Чтобы перезапускать анимацию при частых поеданиях
  const [isEatGlitching, setIsEatGlitching] = useState(false); // Включена ли анимация прямо сейчас

  const [finalData, setFinalData] = useState(null);

  // Фаза смерти: 'none' -> 'shaking' -> 'dissolving' -> (Game Over)
  const [deathPhase, setDeathPhase] = useState('none');

  // --- REFS (GAME LOOP) ---
  const gameState = useRef({
    snake: [{ x: center, y: center }, { x: center, y: center + 1 }, { x: center, y: center + 2 }],
    currentDirection: { x: 0, y: -1 },
    inputQueue: [],
    lastEnqueuedDirection: { x: 0, y: -1 },
    speed: initialSession.speed || 150,
    lastTime: 0,
    timeAccumulator: 0,
    isPaused: false,
    isGameOver: false,
    isDying: false,
    isGameActive: false,
    foodRef: { x: Math.floor(gridSize / 3), y: Math.floor(gridSize / 3) },
    // ИСПРАВЛЕНИЕ 3: Счетчик нажатий в одну сторону для буфера
    sameDirectionPressCount: 0,
    // ИСПРАВЛЕНИЕ 1: Добавляем метку времени последнего укуса
    lastEatTime: 0
    // lastBoostTime убрали, чтобы не ограничивать клавиатуру
  });

  const requestRef = useRef();
  const dissolveIntervalRef = useRef();

  // Хранит количество растворенных сегментов, чтобы текст не смещался
  const dissolvedCountRef = useRef(0);

  // Ref для таймера сброса эффекта буста (чтобы не мигало при частом нажатии)
  const boostTimeoutRef = useRef(null);

  // ИСПРАВЛЕНИЕ: Ref для таймера отключения глитча
  const eatGlitchTimeoutRef = useRef(null);

  // Ref для игрового контейнера (для жестов)
  const containerRef = useRef(null);

  // --- LOGIC ---

  const handleStartGame = () => {
    
    window.croco?.haptic?.('success');
    setIsGameStarted(true);

    // ДОБАВИТЬ ЭТУ СТРОКУ:
    gameState.current.isGameActive = true;

    gameState.current.lastTime = performance.now();
    // Инициализируем сервер (если еще не было, но обычно init делается при маунте для получения настроек)
    // Но здесь мы просто снимаем блокировку рендера и инпута
  };

  const generateFood = (currentSnake) => {
    // Ограничиваем попытки, чтобы не зависнуть, если поле полное
    for (let attempts = 0; attempts < 100; attempts++) {
      const newFood = {
        x: Math.floor(Math.random() * gridSize),
        y: Math.floor(Math.random() * gridSize)
      };
      const onSnake = currentSnake.some(seg => seg.x === newFood.x && seg.y === newFood.y);
      if (!onSnake) return newFood;
    }
    return null; // Победа/Конец игры, если места нет
  };

  // Инициализация
  useEffect(() => {
    if (!socket) return;

    // Запрос инициализации
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    // Установка начальной скорости из настроек (если пришли)
    const initialSession = store.ref.info.game.arcadePublic?.session || {};
    if (initialSession.speed) {
        gameState.current.speed = initialSession.speed;
    }

    const handleEvent = (data) => {
      if (data.type === 'eat_confirmed') {
         // Сервер подтвердил поедание и возможно увеличил сложность
         if (data.speed) gameState.current.speed = data.speed;
      }
      else if (data.type === 'finish') {
         setFinalData(data.finalData);
      }
      else if (data.type === 'game_over') {
         // Время вышло
         setFinalData(data.finalData || { my: data.my, leaderboard: data.leaderboard });

         // Ставим на паузу, но не убиваем
         if (!gameState.current.isGameOver) {
             gameState.current.isPaused = true;
             setIsPaused(true);
             setShowResults(true);
             window.croco?.haptic?.('warning');
         } else {
             // Если уже мертв
             setIsGameOver(true);
         }
      }
    };

    socket.on('arcade:event', handleEvent);

    // Запуск цикла
    gameState.current.lastTime = performance.now();
    requestRef.current = requestAnimationFrame(gameLoop);

    return () => {
        socket.off('arcade:event', handleEvent);
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        if (dissolveIntervalRef.current) clearInterval(dissolveIntervalRef.current);
        if (boostTimeoutRef.current) clearTimeout(boostTimeoutRef.current);
        if (eatGlitchTimeoutRef.current) clearTimeout(eatGlitchTimeoutRef.current);
    };
  }, []);

 // Рассчитываем скорость волны
  // Если змея длинная (>10), волна летит быстрее (0.015с на сегмент), иначе медленно (0.04с)
  const getGlitchSpeed = (length) => {
    return length > 15 ? 0.015 : 0.04;
};

const triggerEatGlitch = (snakeLength) => {
    setEatSeq(s => s + 1);
    setIsEatGlitching(true);

    if (eatGlitchTimeoutRef.current) clearTimeout(eatGlitchTimeoutRef.current);

    const speed = getGlitchSpeed(snakeLength);
    // Время работы эффекта = (Длина * скорость) + время самой анимации (300мс) + небольшой запас
    const duration = (snakeLength * speed * 1000) + 400;

    eatGlitchTimeoutRef.current = setTimeout(() => {
        setIsEatGlitching(false);
    }, duration);
};

  // --- ЯДРО ДВИЖЕНИЯ (ВЫНЕСЕНО ИЗ updatePhysics) ---
  // Возвращает false, если игра закончилась (столкновение)
  const processMoveStep = (direction) => {
      const st = gameState.current;

      const head = { ...st.snake[0] };
      head.x += direction.x;
      head.y += direction.y;

      // 1. Коллизии
      if (head.x < 0 || head.x >= gridSize || head.y < 0 || head.y >= gridSize) {
          handleCrash();
          return false;
      }
      if (st.snake.some(seg => seg.x === head.x && seg.y === head.y)) {
          handleCrash();
          return false;
      }

      // 2. Движение
      const newSnake = [head, ...st.snake];

      if (!st.foodRef) st.foodRef = { x: 5, y: 5 };

      if (head.x === st.foodRef.x && head.y === st.foodRef.y) {
          // EAT
          window.croco?.haptic?.('success');

          // ИСПРАВЛЕНИЕ 2: Запоминаем время поедания для точного расчета анимации головы
          st.lastEatTime = performance.now();

          // ЗАПУСК ГЛИТЧА
          triggerEatGlitch(newSnake.length);

          const nextF = generateFood(newSnake);
          if (nextF) {
              st.foodRef = nextF;
              setFood(nextF);
          } else {
              handleCrash();
              return false;
          }
          setScore(s => s + 1);
          store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'eat' });
      } else {
          // Move (удаляем хвост)
          newSnake.pop();
      }

      st.snake = newSnake;
      setSnake(newSnake);
      return true;
  };

  // --- GAME LOOP ---
  const gameLoop = useCallback((time) => {
    requestRef.current = requestAnimationFrame(gameLoop);

    // ИЗМЕНИТЬ ПРОВЕРКУ ЗДЕСЬ:
    // Было: if (!isGameStarted) return;
    if (!gameState.current.isGameActive) return;

    const st = gameState.current;
    if (st.isGameOver || st.isPaused) {
        st.lastTime = time;
        return;
    }

    const deltaTime = time - st.lastTime;
    st.lastTime = time;
    st.timeAccumulator += deltaTime;

    if (st.timeAccumulator >= st.speed) {
        st.timeAccumulator = 0;
        updatePhysics();
    }
  }, []); // <--- ЗАВИСИМОСТИ ТЕПЕРЬ ПУСТЫЕ

  const updatePhysics = () => {
      const st = gameState.current;

      // Обработка очереди поворотов
      if (st.inputQueue.length > 0) {
          const nextDir = st.inputQueue.shift();
          st.currentDirection = nextDir;
      }

      // Выполнение шага
      processMoveStep(st.currentDirection);
  };

  // --- ОБНОВЛЕННАЯ ЛОГИКА СМЕРТИ ---
  const handleCrash = () => {
      if (gameState.current.isDying) return;

      gameState.current.isDying = true;
      setDeathPhase('shaking');
      window.croco?.haptic?.('error');

      store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'finish' });

      // Задержка перед растворением (время "тряски")
      setTimeout(() => {
          setDeathPhase('dissolving');

          // НОВОЕ: Рассчитываем скорость растворения
          const currentLength = gameState.current.snake.length;

          // Мы хотим, чтобы вся змея исчезла примерно за 1 секунду (1000мс).
          // Интервал = 1000 / длину.
          // Но ставим ограничения:
          // 1. Не медленнее 100мс (для коротких змей, чтобы было видно сегменты)
          // 2. Не быстрее 30мс (чтобы анимация успевала рендериться)
          const calcSpeed = 1000 / currentLength;
          const dissolveSpeed = Math.max(30, Math.min(100, calcSpeed));

          dissolveIntervalRef.current = setInterval(() => {
              setSnake(prev => {
                  if (prev.length <= 1) {
                      clearInterval(dissolveIntervalRef.current);
                      finalizeGameOver();
                      return [];
                  }
                  dissolvedCountRef.current += 1;
                  const remaining = prev.slice(1);
                  return remaining;
              });
              // Вибрация при исчезновении (опционально можно убрать для очень быстрых скоростей)
              if (dissolveSpeed > 40) {
                 window.croco?.haptic?.('selection');
              }
          }, dissolveSpeed); // Используем динамическую скорость
      }, 600);
  };

  const finalizeGameOver = () => {
      gameState.current.isGameOver = true;
      setIsGameOver(true); // Теперь показываем финальный экран
  };

  // --- ОБНОВЛЕННАЯ ЛОГИКА ВВОДА С БУФЕРОМ ---
  const handleInput = useCallback((x, y) => {
      if (!isGameStarted) return;
      const st = gameState.current;
      if (st.isGameOver || st.isPaused) return;

      const lastDir = st.inputQueue.length > 0
          ? st.lastEnqueuedDirection
          : st.currentDirection;

      // 1. ИГРОК НАЖАЛ ТУ ЖЕ КНОПКУ (НАПРАВЛЕНИЕ)
      if (lastDir.x === x && lastDir.y === y) {
          // Увеличиваем счетчик нажатий в эту сторону
          st.sameDirectionPressCount += 1;

          // ИСПРАВЛЕНИЕ 3: Буст работает только начиная со 2-го нажатия
          // Первое нажатие - "холостое" (буфер)
          const performanceNow = performance.now();

          // Не бустить, если только что повернули (защита от дребезга)
          if (st.lastDirectionChangeTime + 200 > performanceNow) {
              return;
          }
          // Троттлинг по времени УБРАН, так как пользователь хочет спамить с клавиатуры

          // Если дошли сюда, значит count >= 2 -> выполняем БУСТ
          for (let i = 0; i < BOOST_STEPS; i++) {
               const isAlive = processMoveStep({x, y});
               if (!isAlive) break;
          }

          st.timeAccumulator = 0;
          st.lastTime = performanceNow;

          activateBoostEffect();
          window.croco?.haptic?.('impactMedium');

          return;
      }

      // 2. ИГРОК СМЕНИЛ НАПРАВЛЕНИЕ (ПОВОРОТ)

      // Нельзя развернуться на 180
      if (lastDir.x === -x && lastDir.y === -y) return;
      if (st.inputQueue.length >= 2) return;

      const newDir = { x, y };
      st.inputQueue.push(newDir);
      st.lastEnqueuedDirection = newDir;

      // ИСПРАВЛЕНИЕ 3: Сброс счетчика
      // Мы сменили направление, поэтому считаем это нажатие "первым" (count = 1) для новой стороны
      st.sameDirectionPressCount = 0;
      st.lastDirectionChangeTime = performance.now();

      window.croco?.haptic?.('selection');

  }, [isGameStarted]);

  // Функция включения эффекта свечения
  const activateBoostEffect = () => {
      setIsBoosting(true);

      // Сбрасываем предыдущий таймер, если игрок часто жмет буст
      if (boostTimeoutRef.current) {
          clearTimeout(boostTimeoutRef.current);
      }

      // Выключаем через 0.5 сек
      boostTimeoutRef.current = setTimeout(() => {
          setIsBoosting(false);
          boostTimeoutRef.current = null;
      }, 500);
  };

  const togglePause = useCallback((forceState = null) => {

    if (!isGameStarted) {
      handleStartGame ();
      return;
    };
    // Пауза не работает, если игра не начата или уже кончилась
    if (!isGameStarted && forceState === null) return;
    if (gameState.current.isGameOver && !showResults) return; // Если мертв, пауза не работает (только репорт)

    const newState = forceState !== null ? forceState : !gameState.current.isPaused;
    gameState.current.isPaused = newState;
    setIsPaused(newState);

    if (!newState) {
        gameState.current.lastTime = performance.now();
    } else {
        window.croco?.haptic?.('impactLight');
    }
}, [isGameStarted, showResults]);

  // Клавиатура
  useEffect(() => {
      const handleKey = (e) => {
          // Используем e.code для игнорирования раскладки клавиатуры
          switch(e.code) {
              case 'ArrowUp':
              case 'KeyW':
                  handleInput(0, -1);
                  break;

              case 'ArrowDown':
              case 'KeyS':
                  handleInput(0, 1);
                  break;

              case 'ArrowLeft':
              case 'KeyA':
                  handleInput(-1, 0);
                  break;

              case 'ArrowRight':
              case 'KeyD':
                  handleInput(1, 0);
                  break;

              case 'Space':
              case 'Escape':
              case 'Enter':
              case 'NumpadEnter':
                  togglePause();
                  break;

              default: break;
          }
      };
      window.addEventListener('keydown', handleKey);
      return () => window.removeEventListener('keydown', handleKey);
  }, [handleInput, togglePause]);

  // --- GESTURE CONTROLLER ---
  useEffect(() => {
    // ВЕРНУЛИ НА document.body (ВЕСЬ ЭКРАН)
    const controller = new GestureController(document.body, {
        continuous: false,
        sensitivityX: 30,
        sensitivityY: 30,
        disableDiagonal: true,
        // Исключаем кнопки
        excludeSelectors: ['.sn-btn', '.sn-pause-btn', '.sn-start-btn', '.tr-btn', 'button']
    });

    controller.onSwipe = (dir, dist, swipeCount) => {
        let reqX = 0, reqY = 0;
        switch (dir) {
            case 'up': reqX = 0; reqY = -1; break;
            case 'down': reqX = 0; reqY = 1; break;
            case 'left': reqX = -1; reqY = 0; break;
            case 'right': reqX = 1; reqY = 0; break;
        }

        const st = gameState.current;
        const currentDir = st.inputQueue.length > 0 ? st.lastEnqueuedDirection : st.currentDirection;
        const isSameDir = (currentDir.x === reqX && currentDir.y === reqY);

        // ЛОГИКА: БУСТ ТОЛЬКО НА ПЕРВЫЙ ЖЕСТ
        // Если жест совпадает с направлением (это буст), но это уже не первый свайп в серии -> ОТМЕНА
        if (isSameDir && swipeCount > 1) {
             return;
        }

        // Иначе выполняем (поворот разрешен всегда, первый буст разрешен)
        handleInput(reqX, reqY);
    };

    // Тап для паузы
    controller.onTap = () => {
         // Можно сделать паузу по тапу в центре, но лучше не перегружать.
         // Оставим пустым или добавим логику, если нужно.
         // Например, если игра не начата - старт.
         if (!isGameStarted) handleStartGame();
    };

    return () => controller.destroy();
  }, [handleInput, isGameStarted]);

  // Функция "Остаться"
  const handleStay = () => {
      setShowResults(false);
      togglePause(false);
  };

  // --- RENDER ---

  // 1. СТАРТОВЫЙ ЭКРАН
  if (!isGameStarted) {
      return <SnakeStartScreen t={t} onStart={handleStartGame} />;
  }

  // 2. ФИНАЛ
  if (isGameOver || showResults) {
      const myStats = finalData?.my || {};
      const finalScore = myStats.score !== undefined ? myStats.score : score;
      const rank = myStats.rank || 'snake_rank_script_kiddie';

      const isDead = isGameOver;
      const reportTitle = isDead ? t('arcade.snake_crash', 'СИГНАЛ ПОТЕРЯН') : t('arcade.time_up', 'ВРЕМЯ ИСТЕКЛО');
      const stampText = isDead ? t('arcade.snake_status_killed', 'УНИЧТОЖЕН') : t("arcade.snake_status_extracted", "ИЗВЛЕЧЕН");
      const stampClass = isDead ? "tr-stamp-fail" : "tr-stamp-ok";

      return (
        <div className="tr-report-overlay">
           <div className="tr-report-card">
               <div className="tr-watermark">CYBER</div>
               <div className="tr-report-header">
                  <div className="tr-report-title">{reportTitle}</div>
               </div>
               <div className="tr-stats-grid">
                   <div className="tr-stat-row">
                       <span className="tr-stat-label">{t('arcade.wordduel_rank', 'ЗВАНИЕ')}</span>
                       <span className="tr-stat-value rank-text">{t(`arcade.${rank}`, rank)}</span>
                   </div>
                   <div className="tr-stat-row" style={{borderBottom:'none'}}>
                       <span className="tr-stat-label">{t('arcade.snake_data_collected', 'ДАННЫЕ')}</span>
                       <span className="tr-stat-value">{finalScore} <span style={{fontSize:'12px'}}>TB</span></span>
                   </div>
               </div>
               <div className={`tr-result-stamp ${stampClass}`}>
                   {stampText}
               </div>

               {/* Вывод собранного текста (Пасхалка) */}
               <div className="tr-stat-row" style={{flexDirection:'column', borderBottom:'none', background:'rgba(0,0,0,0.05)', padding:'8px', borderRadius:'4px'}}>
                   <span className="tr-stat-label" style={{marginBottom:'4px'}}>DECRYPTED_DATA:</span>
                   <span style={{fontFamily:'monospace', fontSize:'11px', color:'#333', fontStyle:'italic'}}>
                       "{currentQuote.substring(0, finalScore + 3)}..."
                   </span>
               </div>
               <div style={{width: '100%', marginBottom: '15px'}}>
                   {finalData && (
                       <UnifiedLeaderboard
                          results={finalData.leaderboard}
                          myId={store.ref.telegram.telegramUserId}
                          t={t}
                       />
                   )}
               </div>
               <div className="tr-btn-group">
                   {!isDead && (
                       <button className="tr-btn tr-btn-secondary" onClick={handleStay}>
                           <FaPlay style={{marginRight: '8px'}} /> {t('arcade.stay', 'ОСТАТЬСЯ')}
                       </button>
                   )}
                   <button className="tr-btn tr-btn-primary" onClick={onClose}>
                       <FaTimes style={{marginRight: '8px'}} /> {t('arcade.close', 'ЗАКРЫТЬ')}
                   </button>
               </div>
           </div>
        </div>
      );
  }

  // Получить букву для сегмента змеи
  // Индекс 0 в массиве snake = ГОЛОВА.
  // Мы хотим, чтобы текст читался от хвоста к голове (так логичнее при движении: новые буквы добавляются в начало строки, то есть в голову).
  // Голова = quote[snake.length - 1] (Самая "свежая" буква)
  // Хвост = quote[0] (Самая "старая" буква)
  // Еда = quote[snake.length] (Следующая буква)

  // --- TEXT LOGIC (UPDATED) ---

  // snakeIndex - это индекс в текущем массиве snake.
  // dissolvedCountRef - сколько сегментов (голов) мы уже удалили.
  // Изначально: Голова(0) = Цитата(0).
  // После удаления одной головы:
  // Новая Голова (была 1, стала 0) = должна быть Цитата(1).
  // Формула: 0 + 1 = 1. Верно.
  const getLetterForBodyPart = (snakeIndex) => {
      const effectiveIndex = snakeIndex + dissolvedCountRef.current;
      return currentQuote[effectiveIndex] || '';
  };

  // Еда показывает следующую букву, которая присоединится к хвосту.
  // Если длина 3 ("HEL"), следующая буква (индекс 3) - "L".
  const getLetterForFood = () => {
      // Длина змеи + сколько уже удалено = индекс следующей буквы
      const len = snake.length + dissolvedCountRef.current;
      return currentQuote[len] || '';
  };

  // Рендер сетки
  const renderGrid = () => {
      const cells = [];
      const glitchSpeed = getGlitchSpeed(snake.length);

      // ИСПРАВЛЕНИЕ 3: Вычисляем время, прошедшее с укуса, прямо в рендере
      // Это нужно, чтобы отключить анимацию на голове, когда она уже "проморгалась"
      const timeSinceEat = performance.now() - gameState.current.lastEatTime;
      const animDuration = 300; // ms (длительность CSS анимации sn-eat-glitch)

      for (let y = 0; y < gridSize; y++) {
          for (let x = 0; x < gridSize; x++) {
              let className = 'sn-cell';
              let content = null;

              let style = { fontSize };

              const snakeIndex = snake.findIndex(s => s.x === x && s.y === y);

              if (snakeIndex !== -1) {
                  className += snakeIndex === 0 ? ' sn-snake-head' : ' sn-snake-body';

                  // ПРИОРИТЕТ 1: СМЕРТЬ (Глитч распада)
                  if (deathPhase !== 'none') {
                      className += ' dead';

                      // Добавляем хаотичную анимацию смерти
                      style = {
                          ...style,
                          // steps(4) делает движения дергаными (цифровыми), а не плавными
                          animation: 'sn-death-glitch 0.2s steps(4) infinite',
                          // Генерируем "случайную" задержку на основе координат,
                          // чтобы каждый сегмент глючил в своем ритме
                          animationDelay: `${((x + y) * 0.07) % 0.5}s`
                      };
                  }
                  // ПРИОРИТЕТ 2: ГЛИТЧ ПОЕДАНИЯ (Волна)
                  // Работает только если змея жива и сработал триггер
                  else if (isEatGlitching) {
                      if (isBoosting) className += ' boosting'; // Сохраняем цвет буста под глитчем

                      // ИСПРАВЛЕНИЕ 3: Проверяем, находится ли этот конкретный сегмент в фазе глитча
                      // Если (задержка этого сегмента + длительность анимации) < прошедшего времени,
                      // значит анимация на нем уже закончилась, и мы не должны её применять повторно.
                      const delayMs = snakeIndex * glitchSpeed * 1000;
                      const isSegmentGlitchActive = timeSinceEat < (delayMs + animDuration + 100);

                      if (isSegmentGlitchActive) {
                          style = {
                              ...style,
                              animation: 'sn-eat-glitch 0.3s ease-out forwards',
                              animationDelay: `${snakeIndex * glitchSpeed}s`
                          };
                      }
                  }
                  // ПРИОРИТЕТ 3: БУСТ (Вибрация)
                  // Анимация задана в CSS классе .boosting, здесь просто добавляем класс
                  else if (isBoosting) {
                      className += ' boosting';
                  }

                  content = getLetterForBodyPart(snakeIndex);
              }
              else if (food.x === x && food.y === y) {
                  if (deathPhase === 'none' || deathPhase === 'shaking') {
                      className += ' sn-food';
                      content = getLetterForFood();
                  }
              }

              // Ключ для ре-рендера элемента при изменении состояния
              const cellKey = snakeIndex !== -1
                  ? `${x}-${y}-${eatSeq}-${deathPhase}` // Добавили deathPhase в ключ, чтобы при смерти анимация точно сбросилась и началась новая
                  : `${x}-${y}`;

              cells.push(
                  <div key={cellKey} className={className} style={style}>
                      {content}
                  </div>
              );
          }
      }
      return cells;
  };

  // 3. ИГРА
  return (
    <div className="snake-container" ref={containerRef} style={{ touchAction: 'none' }}>

        <div className="snake-header">
            <div className="snake-stat-box">
                <span className="snake-label">{t('arcade.snake_score_label', 'ДАННЫЕ')}</span>
                <span className="snake-value score">{score}</span>
            </div>
            <div className="snake-title" style={{color:'#00ff41', fontWeight:'900', letterSpacing:'2px'}}>
                <FaMicrochip style={{marginRight:'5px'}}/> CYBER WORM
            </div>
            <div className="snake-stat-box">
                <span className="snake-label">{t('arcade.time_label', 'ТАЙМЕР')}</span>
                <span className="snake-value">
                    {(timeLeft / 60) | 0}:{(timeLeft % 60).toString().padStart(2, '0')}
                </span>
            </div>
        </div>

        {/* Добавляем класс shaking, если фаза смерти shaking */}
        {/* Сетка использует gridSize для колонок и строк */}
        <div className={`snake-grid ${deathPhase === 'shaking' ? 'shaking' : ''}`} style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridTemplateRows: `repeat(${gridSize}, 1fr)`
        }}>

            {/* MATRIX RAIN ТЕПЕРЬ ВНУТРИ GRID */}
            <MatrixRain />

            {isPaused && (
                <div className="snake-overlay">
                    <div className="snake-paused-text">{t('arcade.paused', 'ПАУЗА')}</div>
                </div>
            )}
            {renderGrid()}
        </div>

        {/* --- УПРАВЛЕНИЕ (GRID, КАК В ТЕТРИСЕ) --- */}
        <div className="snake-controls-wrapper">
             {/* Верхний ряд */}
             <div className="sn-btn sn-up" onPointerDown={() => handleInput(0, -1)}>
                <FaArrowUp />
             </div>

             <div className={`sn-pause-btn sn-pause ${isPaused ? 'paused' : ''}`} onClick={() => togglePause()}>
                 {isPaused ? <FaPlay /> : <FaPause />}
             </div>

             {/* Нижний ряд */}
             <div className="sn-btn sn-left" onPointerDown={() => handleInput(-1, 0)}>
                <FaArrowLeft />
             </div>
             <div className="sn-btn sn-down" onPointerDown={() => handleInput(0, 1)}>
                <FaArrowDown />
             </div>
             <div className="sn-btn sn-right" onPointerDown={() => handleInput(1, 0)}>
                <FaArrowRight />
             </div>
        </div>
    </div>
  );
};

export default Snake;